import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import './Project.css';
import SEO from '../../components/SEO';
import { allProjects, projectFilters } from './projectData';

const DEFAULT_VISIBLE_COUNT = 12;
const LOAD_MORE_COUNT = 6;
const LG_MIN_WIDTH = '(min-width: 1024px)';

function shuffleProjects(projects) {
  const copy = [...projects];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

function useIsLargeScreen() {
  const [isLarge, setIsLarge] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(LG_MIN_WIDTH).matches : true
  );

  useEffect(() => {
    const mql = window.matchMedia(LG_MIN_WIDTH);
    const onChange = () => setIsLarge(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return isLarge;
}

const Project = ({ showSEO = true }) => {
  const [isMounted, setIsMounted] = useState(false);
  const [activeFilter, setActiveFilter] = useState('All');
  const [visibleCount, setVisibleCount] = useState(DEFAULT_VISIBLE_COUNT);
  const [isFiltering, setIsFiltering] = useState(false);
  const [mobileRevealedId, setMobileRevealedId] = useState(null);
  const isLargeScreen = useIsLargeScreen();

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    setMobileRevealedId(null);
  }, [activeFilter]);

  useEffect(() => {
    if (isLargeScreen) {
      setMobileRevealedId(null);
    }
  }, [isLargeScreen]);

  useEffect(() => {
    if (mobileRevealedId == null) return;

    const onDocClick = (e) => {
      if (!e.target.closest('[data-project-card-root]')) {
        setMobileRevealedId(null);
      }
    };

    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [mobileRevealedId]);

  const filteredProjects = useMemo(() => {
    if (activeFilter === 'All') {
      return isMounted ? shuffleProjects(allProjects) : allProjects;
    }
    return allProjects.filter((project) => project.folderLabel === activeFilter);
  }, [activeFilter, isMounted]);

  const featuredProject = useMemo(
    () =>
      filteredProjects.find((project) => project.featured) ||
      filteredProjects[0] ||
      null,
    [filteredProjects]
  );

  const gridProjects = useMemo(
    () => filteredProjects.filter((project) => project.id !== featuredProject?.id),
    [filteredProjects, featuredProject]
  );

  const visibleProjects = useMemo(
    () => gridProjects.slice(0, visibleCount),
    [gridProjects, visibleCount]
  );

  const hasMoreProjects = visibleCount < gridProjects.length;

  const handleFilterChange = (filter) => {
    if (filter === activeFilter) return;

    setIsFiltering(true);
    setActiveFilter(filter);
    setVisibleCount(DEFAULT_VISIBLE_COUNT);

    setTimeout(() => {
      setIsFiltering(false);
    }, 240);
  };

  const handleLoadMore = () => {
    setVisibleCount((current) => current + LOAD_MORE_COUNT);
  };

  return (
    <div className="project-page">
      {showSEO && (
        <SEO
          title="Interior Design Projects Portfolio | Techie Interiors Nairobi"
          description="Browse Techie Interiors' portfolio of completed interior design projects in Nairobi Kenya — gypsum ceilings, TV wall units, wainscoting panels, kitchen fit-outs, and residential transformations."
          image="/techLogo.jpeg"
          url="https://techieinteriors.co.ke/project"
          structuredData={{
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "Interior Design Projects Portfolio — Techie Interiors",
            "description": "Browse completed interior design projects by Techie Interiors in Nairobi Kenya, including gypsum ceilings, TV wall units, kitchen fit-outs, wainscoting panels, and residential transformations.",
            "url": "https://techieinteriors.co.ke/project",
            "isPartOf": {
              "@type": "WebSite",
              "name": "Techie Interiors",
              "url": "https://techieinteriors.co.ke"
            }
          }}
        />
      )}

      <header className="project-hero">
        <div className="project-hero-content">
          <h1>{activeFilter === 'All' ? 'Our Portfolio' : `${activeFilter} Projects`}</h1>
          <p>Explore our latest interior design and fit-out work.</p>
        </div>
      </header>

      <div className="project-filter-bar-wrap">
        <nav className="project-filter-bar" aria-label="Project category filters">
          {projectFilters.map((filter) => (
            <button
              key={filter}
              type="button"
              className={`filter-pill ${activeFilter === filter ? 'active' : ''}`}
              onClick={() => handleFilterChange(filter)}
            >
              {filter}
            </button>
          ))}
        </nav>
      </div>

      <main className={`project-content ${isFiltering ? 'is-filtering' : ''}`}>
        {featuredProject && (
          <section className="featured-project">
            <div className="featured-media">
              {featuredProject.type === 'video' ? (
                <video controls muted playsInline preload="none">
                  <source src={featuredProject.media} type="video/mp4" />
                </video>
              ) : (
                <img
                  src={featuredProject.media}
                  alt={featuredProject.title}
                  fetchpriority="high"
                  decoding="async"
                />
              )}
            </div>
            <div className="featured-content">
              <span className="featured-label">Featured Project</span>
              <h2>{featuredProject.title}</h2>
              <p>
                {featuredProject.folderLabel} - {featuredProject.details}
              </p>
            </div>
          </section>
        )}

        {isFiltering ? (
          <section className="project-grid skeleton-grid">
            {Array.from({ length: 6 }).map((_, index) => (
              <article className="project-card skeleton-card" key={`skeleton-${index}`}>
                <div className="skeleton-media" />
                <div className="skeleton-line" />
                <div className="skeleton-line short" />
              </article>
            ))}
          </section>
        ) : (
          <section className="project-grid">
            {visibleProjects.map((project) => {
              const isRevealed = mobileRevealedId === project.id;

              return (
                <article
                  key={project.id}
                  data-project-card-root
                  className={`project-card ${isRevealed ? 'project-card--reveal' : ''}`}
                  aria-expanded={isLargeScreen ? undefined : isRevealed}
                  onClick={
                    isLargeScreen
                      ? undefined
                      : () => setMobileRevealedId(project.id)
                  }
                >
                  {isLargeScreen ? (
                    <Link
                      className="project-card-link"
                      to={`/project/${project.id}`}
                      aria-label={`View project ${project.title}`}
                    >
                      <div className="project-media">
                        {project.type === 'video' ? (
                          <video muted playsInline preload="none" aria-hidden="true">
                            <source src={project.media} type="video/mp4" />
                          </video>
                        ) : (
                          <img
                            src={project.media}
                            alt={project.title}
                            loading="lazy"
                            decoding="async"
                          />
                        )}
                        <div className="project-overlay">
                          <span>View Project</span>
                          <small>{project.details}</small>
                        </div>
                        <div className="project-card-content">
                          <h3>{project.title}</h3>
                          <p>{project.folderLabel}</p>
                        </div>
                      </div>
                    </Link>
                  ) : (
                    <div className="project-card-link project-card-link--static">
                      <div className="project-media">
                        {project.type === 'video' ? (
                          <video muted playsInline preload="none" aria-hidden="true">
                            <source src={project.media} type="video/mp4" />
                          </video>
                        ) : (
                          <img
                            src={project.media}
                            alt={project.title}
                            loading="lazy"
                            decoding="async"
                          />
                        )}
                        <div className="project-overlay">
                          <Link
                            className="project-overlay-cta"
                            to={`/project/${project.id}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            View Project
                          </Link>
                          <small>{project.details}</small>
                        </div>
                        <div className="project-card-content">
                          <h3>{project.title}</h3>
                          <p>{project.folderLabel}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </article>
              );
            })}
          </section>
        )}

        {!isFiltering && hasMoreProjects && (
          <div className="load-more-wrap">
            <button type="button" className="load-more-btn" onClick={handleLoadMore}>
              Load More
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default Project;